import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

const EMPTY = { imageUrl: "", caption: "", linkUrl: "https://instagram.com", order: 0, active: true };

export default function AdminInstagram() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const refresh = async () => {
        const { data } = await api.get("/admin/instagram-tiles");
        setItems(data || []);
    };
    useEffect(() => { refresh(); }, []);

    const startCreate = () => {
        const nextOrder = items.length ? Math.max(...items.map((i) => i.order)) + 1 : 0;
        setEditing({ ...EMPTY, order: nextOrder });
        setOpen(true);
    };
    const startEdit = (t) => { setEditing({ ...EMPTY, ...t }); setOpen(true); };
    const close = () => { setOpen(false); setEditing(null); };

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = { ...editing, order: Number(editing.order) || 0 };
            if (editing.id) await api.patch(`/admin/instagram-tiles/${editing.id}`, payload);
            else await api.post("/admin/instagram-tiles", payload);
            toast.success(editing.id ? "Tile updated" : "Tile added");
            close();
            refresh();
        } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed"); }
        finally { setBusy(false); }
    };

    const remove = async (t) => {
        if (!window.confirm("Delete this tile?")) return;
        try { await api.delete(`/admin/instagram-tiles/${t.id}`); refresh(); toast.success("Deleted"); }
        catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed"); }
    };

    const toggleActive = async (t) => {
        await api.patch(`/admin/instagram-tiles/${t.id}`, { active: !t.active });
        refresh();
    };

    return (
        <div data-testid="admin-instagram-page">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                <div>
                    <span className="label-eyebrow">Social</span>
                    <h1 className="mt-2 font-display text-4xl text-inkDark">Instagram</h1>
                    <p className="mt-1 text-inkMuted">{items.length} tiles in the strip.</p>
                </div>
                <button onClick={startCreate} className="btn-pill text-sm" data-testid="admin-add-instagram-button"><Plus size={14} /> Add tile</button>
            </div>

            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {items.map((t) => (
                    <div key={t.id} className="bg-ivory border border-inkDark/5 rounded-2xl overflow-hidden" data-testid={`admin-instagram-${t.id}`}>
                        <div className="aspect-square bg-beige">
                            {t.imageUrl ? <img src={t.imageUrl} alt="" className="w-full h-full object-cover" /> : null}
                        </div>
                        <div className="p-3 space-y-2">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-[10px] px-2 py-0.5 rounded-full ${t.active ? "bg-green-50 text-green-700" : "bg-inkDark/5 text-inkMuted"}`}>{t.active ? "Live" : "Hidden"}</span>
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-beige text-inkDark">order {t.order}</span>
                            </div>
                            <div className="flex items-center justify-end gap-1">
                                <button onClick={() => toggleActive(t)} className="text-xs px-2 py-1 rounded-lg hover:bg-beige" data-testid={`admin-instagram-toggle-${t.id}`}>{t.active ? "Hide" : "Show"}</button>
                                <button onClick={() => startEdit(t)} className="p-1.5 rounded-lg hover:bg-beige" data-testid={`admin-instagram-edit-${t.id}`}><Pencil size={14} /></button>
                                <button onClick={() => remove(t)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-600" data-testid={`admin-instagram-delete-${t.id}`}><Trash2 size={14} /></button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {open && editing ? (
                <div className="fixed inset-0 z-50 bg-inkDark/40 flex items-center justify-center p-4" onClick={close}>
                    <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-ivory w-full max-w-xl rounded-2xl shadow-lift" data-testid="admin-instagram-modal">
                        <div className="p-6 border-b border-inkDark/5">
                            <span className="label-eyebrow">{editing.id ? "Edit" : "New"}</span>
                            <h2 className="font-display text-2xl">{editing.id ? "Edit tile" : "Add tile"}</h2>
                        </div>
                        <div className="p-6 space-y-4">
                            <Field label="Image URL">
                                <input required value={editing.imageUrl} onChange={(e) => setEditing({ ...editing, imageUrl: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" data-testid="admin-instagram-image-input" />
                            </Field>
                            <Field label="Caption (optional)">
                                <input value={editing.caption || ""} onChange={(e) => setEditing({ ...editing, caption: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" />
                            </Field>
                            <Field label="Link URL">
                                <input value={editing.linkUrl || ""} onChange={(e) => setEditing({ ...editing, linkUrl: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" />
                            </Field>
                            <Field label="Order">
                                <input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" />
                            </Field>
                        </div>
                        <div className="p-6 border-t border-inkDark/5 flex justify-end gap-3">
                            <button type="button" onClick={close} className="btn-ghost text-sm">Cancel</button>
                            <button type="submit" disabled={busy} className="btn-pill text-sm" data-testid="admin-instagram-save-button">{busy ? "Saving…" : "Save"}</button>
                        </div>
                    </form>
                </div>
            ) : null}
        </div>
    );
}

function Field({ label, children }) {
    return <label className="block"><span className="label-eyebrow">{label}</span><div className="mt-2">{children}</div></label>;
}
